<script setup lang="ts">
import Debug from 'debug'
import clone from 'lodash/clone'
import { useDialog } from 'primevue/usedialog'
import debounce from 'lodash/debounce'
import { RestaurantAreas } from '~/lib/interfaces/restaurant'
import {
  YunoEnrollmentError,
  YunoEnrollmentStatus,
  YunoPaymentSDKResponse,
  YunoPaymentState,
  YunoPaymentStatus,
  YunoPaymentSubStatus,
} from '~/lib/models/Yuno'
import { StoreErrorType, getErrorImage } from '~/lib/models/errors'
import { GTMContentGroup } from '~/lib/models/gtm'
import { ToastType } from '~/lib/models/ui/toast'
import { PromotionKind } from '~/lib/services/promos/promos.dto'
import { IMFiscalData } from '~/lib/services/store/auth.dto'
import { CartTotals } from '~/lib/services/store/catalog/cart.dtos'
import { ProductDTO } from '~/lib/services/store/common.dto'
import {
  LegacyPaymentBehaviour,
  LegacyPaymentMethod,
  dynamicFiscalKey,
} from '~/lib/services/store/country/country.dtos'
import {
  Address,
  CreateOrder,
  CreateOrderBody,
  StoreOrder,
} from '~/lib/services/store/order/order.dto'
import { SpecialPaymentType, YunoMethod } from '~/lib/services/store/payment/payment.dtos'
import { getDeviceId } from '~/utils/deviceUtils'
import { getAnalyticsClientId } from '~/utils/gtmUtils'
import { isPreviousToPayment } from '~/utils/orderUtils'
import { getOrderTotals } from '~/utils/storeUtils'

const debug = Debug(`app:view:CheckoutView`)

const appStore = useAppStore()
const restaurantStore = useRestaurantStore()
const { isFavorite } = useFavoriteRestaurant(restaurantStore.restaurant?.code)
const authStore = useAuthStore()
const countryCode = useCountry()
const isMobile = inject<Ref<boolean>>('isMobile')
const { t } = useLocale()
const { format } = useFormatAmount()
const { cartRoute, navigateToHome, ssoRoute, orderDetailRoute } = useMcdRoutes()
const dialog = useDialog()
const { handleStoreError } = useStoreResponse()
const { initYuno, enrollment, payment: paymentYuno } = useYuno()
const { cancelOrder } = useOrderActions()
const { $toast } = useNuxtApp()
const {
  trackPageView,
  trackOnCloseModal,
  trackAddShippingInfo,
  trackAddPaymentInfo,
  trackSelectPaymentType,
  trackOrderFailed,
  trackFreeShipping,
} = useGTM()
const { paymentServiceRef, orderServiceRef } = useStoreService()
const paymentService = paymentServiceRef()
const orderService = orderServiceRef()

const fetching = ref<boolean>(true)
const loading = ref<boolean>(false)
const paymentInProgress = ref<boolean>(false)
const paymentMethods = ref<YunoMethod[]>([])
const yunoMethod = ref<YunoMethod>()
const legacyPaymentMethod = ref<LegacyPaymentMethod>()
const orderResponseDto = ref<CreateOrder>()
const legacyPaymentData = ref<{ orderId: string; paymentUrl: string }>()
const yunoConfirmation = ref()
const legacyConfirmation = ref()
const freeOrderConfirmation = ref()
// fiscal
const fiscalFields = ref<IMFiscalData>()
const billFields = ref<Record<string, string>>()
// Total
const isVisibleTotal = ref<boolean>(false)
// Sonic Branding
const isPaymentSound = ref<boolean>(false)
// Free Order
const isFreeOrder = computed(() => appStore.isFreeOrder)

provide('yunoMethod', yunoMethod)
provide('legacyPaymentMethod', legacyPaymentMethod)
provide(dynamicFiscalKey, appStore.ecommerceConfig?.dynamicFiscalFields || [])

const cartProductsLength = computed(() => appStore.cart.products.length)
const isMOP = computed(() => {
  if (appStore.currentOrder) return appStore.currentOrder.type === RestaurantAreas.MOP
  return restaurantStore.areaSelected === RestaurantAreas.MOP
})

const buttonLabel = computed(() =>
  isFreeOrder.value ? t('checkout.actions.order') : t('checkout.actions.pay')
)

const disabledPay = computed(() => {
  // Order free
  if (isFreeOrder.value) return false
  if (loading.value || fetching.value || paymentInProgress.value || appStore.fetchingCart)
    return true
  if (isYunoCheckout.value && !yunoMethod.value) return true
  else if (!isYunoCheckout.value && !legacyPaymentMethod.value) return true
  return false
})
const isYunoCheckout = computed(() => {
  // ORder free
  if (isFreeOrder.value) return false
  if (appStore.currentOrder) {
    return !!appStore.currentOrder.paymentYuno
  }
  const restaurant = restaurantStore.restaurant!
  return !!restaurant.enabledYuno
})

const totals: ComputedRef<CartTotals> = computed(() => {
  if (appStore.currentOrder) {
    return getOrderTotals(appStore.currentOrder)
  }
  return appStore.cartTotals
})

const cartProductsIds: ComputedRef<string[]> = computed(() => {
  const productsIds = appStore.currentOrder
    ? appStore.currentOrder.products.map(p => p.id)
    : appStore.cart.products.map(p => p.identifier)
  return removeDuplicates(productsIds)
})

const promoStr = computed(() => {
  return appStore.currentOrder ? getOrderPromoStr(appStore.currentOrder, true) : undefined
})

const mustShowTips = computed(
  () =>
    restaurantStore.tipsVisible &&
    appStore.ecommerceConfig?.tipsOptions.options.length &&
    !isFreeOrder.value
)

const mustShowFiscalFields = computed(() => {
  if (appStore.currentOrder) return false
  return !!(
    appStore.ecommerceConfig?.activeFiscalFields &&
    appStore.ecommerceConfig?.dynamicFiscalFields?.length
  )
})

const mandatoryFiscalFields = computed<'MANDATORY' | 'MANDATORY_ALWAYS' | 'OPTIONAL'>(() => {
  if (!mustShowFiscalFields.value) return 'OPTIONAL'
  const area = restaurantStore.areaSelected!.toLowerCase()
  const minInvoice = appStore.ecommerceConfig?.minInvoice || []
  const areaMinInvoice = minInvoice.find(
    (minInv: { area: string; minInvoiceAmount: number }) => minInv.area === area
  )
  if (!areaMinInvoice) return 'OPTIONAL'
  if (areaMinInvoice.minInvoiceAmount === 0) return 'MANDATORY_ALWAYS'
  const total = appStore.cartTotals.total
  return total >= areaMinInvoice.minInvoiceAmount ? 'MANDATORY' : 'OPTIONAL'
})

const mustShowFiscalCheckoutAlert = computed(() => {
  if (!mustShowFiscalFields.value) return false
  return !!appStore.ecommerceConfig?.checkoutAlert
})

const showInPersonPayment = computed(() => !!appStore.cartBackendAdditional?.inPersonPayment)

const hasFreeShippingPromo = computed(() => {
  if (appStore.currentOrder) return !!appStore.currentOrder.isFreteFree
  return !!cartProductsLength.value && !!appStore.cartBackendAdditional?.isFreteFree
})

const goBack = async () => {
  // go to order detail
  if (appStore.currentOrder) {
    return await navigateTo(orderDetailRoute(appStore.currentOrder.id))
  }
  // go to cart
  return await navigateTo(cartRoute())
}

// new orders
const createNewOrder = async () => {
  // createOrder
  const price = {
    tax: appStore.cartTax,
    total: appStore.cartTotal,
    tip: appStore.cartTipOption,
  }
  const products: ProductDTO[] = appStore.cart.products.map(cartP => {
    const {
      id,
      identifier,
      unit,
      optionsGroups,
      isPromo,
      isPromoApplied,
      productType,
      promotions,
      isPromoFlex,
    } = cartP
    // If product contains an active promotion, return promotions, else return an empty array
    const activePromotion = promotions?.some(promo => promo.active) ? promotions : []
    return {
      id,
      identifier,
      unit,
      optionsGroups,
      isPromo: isPromo || undefined,
      isPromoApplied: isPromoApplied || undefined,
      productType,
      promotions: activePromotion,
      isPromoFlex: isPromoFlex || undefined,
    }
  })
  const isYunoPayment = isYunoCheckout.value
  if (isYunoPayment) {
    // set to send purchase event on back or front
    appStore.setSendPurchaseFront(yunoMethod.value!.sendPurchaseEventByApps)
  }
  let yunoPaymentType: string | undefined
  if (!isYunoPayment && isFreeOrder.value) {
    yunoPaymentType = SpecialPaymentType.PROMOTION
  } else if (isYunoPayment) {
    yunoPaymentType =
      yunoMethod.value!.type === SpecialPaymentType.IN_PERSON
        ? SpecialPaymentType.IN_PERSON
        : paymentMethodToSend(yunoMethod.value!.type)
  } else {
    yunoPaymentType = undefined
  }
  const paymentMethodType = yunoPaymentType || legacyPaymentMethod.value!.code

  // track addPaymentInfo
  trackAddPaymentInfo({
    cartProducts: appStore.cart.products,
    paymentType: paymentMethodType,
    bill: !!billFields.value,
  })
  if (appStore.cartBackendAdditional?.isFreteFree)
    trackFreeShipping({ cartProducts: appStore.cart.products })

  const deliveryAddress = restaurantStore.selectedRestaurant?.addressDLV
    ? clone(restaurantStore.selectedRestaurant.addressDLV)
    : undefined
  // add number to address string
  if (deliveryAddress?.address && deliveryAddress?.number)
    deliveryAddress.address = `${deliveryAddress.address} ${deliveryAddress.number}`
  const addressId: Address | undefined = isMOP.value ? undefined : deliveryAddress

  const appliedPromo = appStore.appliedPromotions?.[0] || undefined
  const coupon = appliedPromo?.kind === PromotionKind.COUPON ? appliedPromo.code : undefined
  const promotionsList = appStore.appliedPromotions?.length
    ? appStore.appliedPromotions.map(promotion => promotion.promotionId)
    : undefined

  const deviceId = getDeviceId() || undefined

  // Send always analytics info into createOrder (purchase back, automatic cancelling orders)
  const clientIdWeb = getAnalyticsClientId()
  const mcId = authStore.userMcId

  const order: CreateOrderBody = {
    price,
    products,
    area: restaurantStore.areaSelected!,
    fiscalFields: billFields.value || {},
    paymentMethod: paymentMethodType,
    paymentYuno: isYunoPayment,
    origin: appStore.appVersion,
    restaurant: restaurantStore.restaurant!.id!,
    addressId,
    originalOrderID: appStore.originalOrderID,
    // promotions
    coupon,
    promotionsList,
    // device
    deviceId,
    // analytics info
    mcId,
    analyticsFields: {
      clientIdWeb,
    },
  }

  const orderResponse: CreateOrder = await orderService.createOrder({
    customerToken: authStore.token!,
    countryCode: appStore.countryCode,
    order,
  })
  // reset originalOrderID
  appStore.originalOrderID = undefined

  // Save address or restaurant into latest used
  if (restaurantStore.selectedRestaurant?.addressDLV) {
    saveAddressLatestAddresses(
      restaurantStore.selectedRestaurant.addressDLV,
      authStore.userMcId!,
      countryCode
    )
  } else {
    saveRestaurantLatestRestaurants({
      restaurant: restaurantStore.restaurant,
      isFavorite: isFavorite.value,
      userMcId: authStore.userMcId!,
      country: countryCode,
    })
  }

  debug('order created', orderResponse)

  const orderId = orderResponse.id
  // fetchPendings
  const pendings = await orderService.getPendingOrdersByUser({
    customerToken: authStore.token!,
    countryCode: appStore.countryCode,
  })
  // store currentOrder on appStore
  const currentOrder = pendings.find(({ id }) => id === orderId)
  appStore.setCurrentOrder(currentOrder)
  // clearCart
  appStore.clearCart()

  // Show thank you page if isFreeOrder
  if (isFreeOrder.value) {
    freeOrderConfirmation.value = orderResponse
    debug('Show thank you page screen', orderResponse)
    return
  }

  // Special payment, decide to continue payment or skip it (inPerson for example)
  const mustContinuePayment = await shouldContinuePayment()
  if (!mustContinuePayment) return null

  debug('start payment after order creation')
  // yunoPayment
  if (isYunoPayment) {
    orderResponseDto.value = orderResponse
    await startYunoPayment(orderId)
  } else if (legacyPaymentMethod.value?.behaviour === LegacyPaymentBehaviour.BROWSER) {
    // legacy payment browser (external)
    const orderUrl = `${window.location.origin}${orderDetailRoute(orderResponse.id)}`
    const openUrl = `${orderResponse.paymentUrl}&orderUrl=${orderUrl}`
    window.open(openUrl, '_self')
  } else {
    // legacy payment webview/webviewFull
    orderResponseDto.value = orderResponse
    const data = {
      orderId,
      paymentUrl: orderResponse.paymentUrl!,
    }
    legacyPaymentData.value = data
  }
}

// pay pending order
const processCurrentOrderPayment = async (order: StoreOrder) => {
  debug('paying an already created order')
  const paymentMethodType = isYunoCheckout.value
    ? paymentMethodToSend(yunoMethod.value!.type)
    : legacyPaymentMethod.value!.code

  // track addPaymentInfo
  trackAddPaymentInfo({
    order: appStore.currentOrder,
    paymentType: paymentMethodType,
    bill: !!order.fiscalFields,
  })
  if (appStore.currentOrder?.isFreteFree) trackFreeShipping({ order: appStore.currentOrder })

  const result = await payPendingOrder(paymentMethodType || '')
  if (result) {
    // yuno payment
    if (isYunoCheckout.value) await startYunoPayment(order.id)
    else {
      // legacy payment
      const orderResponse = orderResponseDto.value!
      if (legacyPaymentMethod.value?.behaviour === LegacyPaymentBehaviour.BROWSER) {
        // legacy payment browser (external)
        const orderUrl = `${window.location.origin}${orderDetailRoute(orderResponse.id)}`
        const openUrl = `${orderResponse.paymentUrl}&orderUrl=${orderUrl}`
        window.open(openUrl, '_self')
        return null
      } else {
        // legacy payment webview/webviewFull
        const data = {
          orderId: orderResponse.id,
          paymentUrl: orderResponse.paymentUrl!,
        }
        legacyPaymentData.value = data
        return null
      }
    }
  } else return null
}

const processOrder = async () => {
  if (!authStore.isUserLogged || !(restaurantStore.isRestaurantSelected || appStore.currentOrder))
    return

  // alert fiscal data mandatory
  if (!fiscalFields.value && mandatoryFiscalFields.value !== 'OPTIONAL') {
    const alertKey = 'checkout.sections.fiscal.alert.mandatory'
    const data = {
      title: t(`${alertKey}.title`),
      body: t(
        `${alertKey}.${
          mandatoryFiscalFields.value === 'MANDATORY_ALWAYS'
            ? 'bodyMandatoryAlways'
            : 'bodyMandatory'
        }`,
        { price: appStore.formattedMinAmount }
      ),
      btnPrimary: t(`${alertKey}.accept`),
    }
    await showDialogAlert({
      dialog,
      data,
    })
    return
  }
  loading.value = true
  try {
    // on retry payment is not a create order only payment
    if (appStore.currentOrder) {
      await processCurrentOrderPayment(appStore.currentOrder)
    } else if (restaurantStore.isRestaurantSelected) {
      await createNewOrder()
    }
  } catch (error: any) {
    debug('🚀 ~ file: CheckoutView.vue:190 ~ createOrder ~ error:', error?.response || error)
    await handleCreateOrderErrors(error)
  } finally {
    loading.value = false
  }
}

const handleCreateOrderErrors = async (error: any) => {
  const { key, status } = handleStoreError(error)

  const isPromoError = key === StoreErrorType.PROMO_ERROR
  const errorKey = (key === StoreErrorType.UNHANDLED ? StoreErrorType.ORDER_GENERIC : key) as string

  // For fiscalFieldsRequired print minInvoiceAmount for this area
  let value = errorKey === StoreErrorType.status674 ? appStore.formattedMinAmount : undefined
  // Image error modal
  const errorImage = getErrorImage(key)
  // limit price order
  if (key === StoreErrorType.status609) {
    value = format(appStore.ecommerceConfig?.limitPriceOrder ?? 0)
  } else if (key === StoreErrorType.status642) {
    // Restaurant without loyalty active
    value = appStore.mdwConfig?.brand?.name ?? "MyMcDonald's"
  }
  let data
  if (isPromoError) {
    const { promoConfigCustomError } = usePromotions()
    data = promoConfigCustomError(status)
    if (!data.btnPrimary) data.btnPrimary = t('common.accept')
  } else {
    data = {
      title: t(`errors.alerts.${errorKey}.title`, { value }),
      body: t(`errors.alerts.${errorKey}.body`, { value }),
      btnPrimary: t(`errors.alerts.${errorKey}.accept`),
      btnSecondary: t(`errors.alerts.${errorKey}.cancel`),
      headerImage: errorImage,
    }
  }

  const result = await showDialogAlert({
    dialog,
    data,
  })

  if (result) {
    // tokenExpired
    if (errorKey === StoreErrorType.status401) {
      await authStore.removeToken()
      return await navigateTo(ssoRoute({ register: false }), { external: true })
    } else if (
      ([StoreErrorType.status605, StoreErrorType.status613] as string[]).includes(errorKey)
    ) {
      await navigateToHome()
    } else if (([StoreErrorType.status618] as string[]).includes(errorKey)) {
      await goBack()
      restaurantStore.openChangeRestaurantModal()
    } else if (
      (
        [
          StoreErrorType.status631,
          StoreErrorType.status636,
          StoreErrorType.status682,
          StoreErrorType.status690,
          StoreErrorType.status679,
        ] as string[]
      ).includes(errorKey)
    ) {
      await goBack()
    } else if (
      (
        [StoreErrorType.status668, StoreErrorType.status669, StoreErrorType.status617] as string[]
      ).includes(errorKey)
    ) {
      await navigateToHome()
    }
    // Payment pending control (619 - orderAlreadyPaid, 629 - paymentPendingApproval)
    else if (
      ([StoreErrorType.status619, StoreErrorType.status629] as string[]).includes(errorKey)
    ) {
      const orderId = appStore.currentOrder?.id || orderResponseDto.value?.id || ''
      await navigateTo(orderDetailRoute(orderId))
    }
  } else if (([StoreErrorType.status617] as string[]).includes(errorKey)) {
    await goBack()
  } else if (errorKey === StoreErrorType.status607 || errorKey === StoreErrorType.status642) {
    restaurantStore.openChangeRestaurantModal()
  }
}

const handlePaymentSdkResponse = (sdkResponse: YunoPaymentSDKResponse) => {
  debug('🚀 ~ file: CheckoutView.vue:250 ~ handlePaymentSdkResponse ~ sdkResponse:', sdkResponse)
  const { status, subStatus, sdkRequiredAction, httpStatusCode } = sdkResponse
  // extra method like mp, do nothing
  if (status === YunoPaymentStatus.READY_TO_PAY && sdkRequiredAction) return

  const confirmationState =
    appStore.ecommerceConfig?.activePreauthCapture &&
    status === YunoPaymentStatus.PENDING &&
    subStatus === YunoPaymentSubStatus.AUTHORIZED
      ? YunoPaymentState.SUCCESS
      : status === YunoPaymentStatus.SUCCEEDED
      ? YunoPaymentState.SUCCESS
      : YunoPaymentState.ERROR

  if (confirmationState === 'ERROR') {
    // order-failed
    const analyticsCode = httpStatusCode === 201 ? '-' : `${httpStatusCode}`
    const type = `${status || YunoPaymentStatus.ERROR}_${
      subStatus || YunoPaymentSubStatus.UNKNOWN
    }`.toLowerCase()
    const paymentType = paymentMethodToSend(yunoMethod.value!.type)
    trackOrderFailed({
      code: analyticsCode,
      type,
      paymentType,
    })
  }
  // payment status data
  const confirmationPaymentData = {
    state: confirmationState,
    status,
    subStatus,
  }
  // Play success payment sound and add a delay to show the success modal
  if (yunoMethod.value?.type === 'CLICK_TO_PAY' && status === YunoPaymentStatus.SUCCEEDED) {
    isPaymentSound.value = true
    setTimeout(() => {
      yunoConfirmation.value = confirmationPaymentData
    }, 600)
    return
  }
  // Show modal if payment is different from click to pay
  yunoConfirmation.value = confirmationPaymentData
}

// Yuno methods
const fetchYunoMethods = async () => {
  if (!authStore.isUserLogged || !(restaurantStore.isRestaurantSelected || appStore.currentOrder))
    return
  try {
    const { paymentMethods: methods } = await paymentService.getPaymentMethodsForCheckout({
      token: authStore.token!,
      country: appStore.countryCode,
      price: totals.value.total,
      products: cartProductsIds.value,
    })
    paymentMethods.value = methods
  } catch (error) {
    debug('🚀 ~ file: CheckoutView.vue:276 ~ fetchYunoMethods ~ error:', error)
  }
}

const reloadYunoMethods = async (autoSelectToken?: string) => {
  fetching.value = true
  await fetchYunoMethods()
  if (autoSelectToken) {
    // auto select just enrolled card as selected (from modifyCards modal)
    const method = paymentMethods.value.find(({ vaultedToken }) => vaultedToken === autoSelectToken)
    if (method) selectMethod(method)
  }
  fetching.value = false
}

const addCard = async () => {
  if (loading.value) return
  try {
    loading.value = true
    // initEnrollment!
    const { customerSession, status } = await paymentService.getInitEnrollment({
      token: authStore.token!,
      country: appStore.countryCode,
      paymentMethodType: 'CARD',
    })
    if (!customerSession || status !== YunoEnrollmentStatus.READY_TO_ENROLL) return
    // enrollment
    const lang = appStore.language.split('_')?.[0]
    const {
      status: enrollmentStatus,
      error,
      vaultedToken: cardToken,
    } = await enrollment(customerSession, lang)

    debug('🚀 ~ file: CheckoutView.vue:309 ~ addCard ~ result:', {
      error,
      vaultedToken: cardToken,
      enrollmentStatus,
    })

    if (enrollmentStatus === YunoEnrollmentStatus.ENROLLED && cardToken) {
      // show toast success
      $toast.show({
        content: t('checkout.sections.paymentMethods.toast.cardSave.success'),
        type: ToastType.SUCCESS,
      })
      // fetch again payment methods for checkout
      fetching.value = true
      await fetchYunoMethods()
      // auto select just enrolled card as selected
      const method = paymentMethods.value.find(({ vaultedToken }) => vaultedToken === cardToken)
      if (method) selectMethod(method)
      fetching.value = false
    } else if (error && error === YunoEnrollmentError.CANCELED_BY_USER) return null
    else {
      // show toast error
      $toast.show({
        content: t('checkout.sections.paymentMethods.toast.cardSave.error'),
        type: ToastType.ERROR,
      })
    }
  } catch (error) {
    debug('🚀 ~ file: CheckoutView.vue:337 ~ addCard ~ error:', error)
  } finally {
    loading.value = false
  }
}

const trackEventSelectPaymentType = (paymentType: string) => {
  const preOrder = appStore.currentOrder
    ? appStore.currentOrder.orderMcDia
    : appStore.cart.products.some(cartProd => cartProd.isMcDia)
  trackSelectPaymentType({ paymentType, preOrder })
}

const isExclusivePayment = computed(() =>
  appStore.currentOrder
    ? appStore.currentOrder.exclusiveEvent
    : appStore.cartBackendAdditional?.exclusivePayment
)

const addressCardProps = computed(() => {
  if (appStore.currentOrder) {
    return {
      order: appStore.currentOrder,
      selectedRestaurant: undefined,
      restaurant: undefined,
    }
  }
  if (restaurantStore.restaurant && restaurantStore.selectedRestaurant) {
    return {
      selectedRestaurant: restaurantStore.selectedRestaurant,
      restaurant: restaurantStore.restaurant,
      order: undefined,
    }
  }
  return undefined
})

const selectMethod = (method?: YunoMethod) => {
  yunoMethod.value = method
  if (method) {
    const yunoPaymentType =
      method.type === SpecialPaymentType.IN_PERSON
        ? SpecialPaymentType.IN_PERSON
        : paymentMethodToSend(method.type)

    trackEventSelectPaymentType(yunoPaymentType)
  }
}

const selectLegacyMethod = (method: LegacyPaymentMethod) => {
  legacyPaymentMethod.value = method
  if (method) trackEventSelectPaymentType(method.code)
}

const startYunoPayment = async (orderId: string) => {
  const pathQueryParam = encodeURIComponent(orderDetailRoute(orderId))
  const pathCallback = `/api/callback/my-orders?path=${pathQueryParam}`
  // open checkoutSession
  const input = {
    price: totals.value.total,
    orderId,
    pathCallback,
  }
  const { checkoutSession } = await paymentService.getCheckoutSession({
    token: authStore.token!,
    country: appStore.countryCode,
    input,
  })

  // set to send purchase event on back or front
  appStore.setSendPurchaseFront(yunoMethod.value!.sendPurchaseEventByApps)

  // pay with yuno
  const lang = appStore.language.split('_')?.[0]
  const paymentSdkResponse = await paymentYuno(
    yunoMethod.value!,
    checkoutSession,
    lang,
    createPayment
  )

  handlePaymentSdkResponse(paymentSdkResponse)
}

const createPayment = async ({
  oneTimeToken,
  checkoutSession,
}: {
  oneTimeToken: string
  checkoutSession: string
}) => {
  try {
    paymentInProgress.value = true
    const body = {
      token: {
        tokenOTT: oneTimeToken,
      },
      orderId: appStore.currentOrder!.id,
      checkOutSession: checkoutSession,
    }
    const response = await paymentService.startPayment({
      token: authStore.token!,
      country: appStore.countryCode,
      data: body,
    })
    const { status, subStatus, checkout } = response
    paymentInProgress.value = false
    return { status, subStatus, checkout, httpStatusCode: 201 }
  } catch (error: any) {
    debug('🚀 ~ file: CheckoutView.vue:397 ~ error:', error)
    return {
      status: YunoPaymentStatus.ERROR,
      subStatus: YunoPaymentSubStatus.ERROR,
      checkout: undefined,
      httpStatusCode: error.response?.status || 500,
    }
  } finally {
    paymentInProgress.value = false
  }
}

const changeCard = () => {
  yunoConfirmation.value = undefined
  trackOnCloseModal()
}

const changeCardLegacy = () => {
  legacyConfirmation.value = undefined
  trackOnCloseModal()
}

const retryPayment = async () => {
  loading.value = true
  yunoConfirmation.value = undefined
  trackOnCloseModal()
  // retry payment, sdk again etc ...
  await startYunoPayment(appStore.currentOrder!.id)
  loading.value = false
}

const onLegacyPaymentResult = (result: string) => {
  legacyConfirmation.value = result
  legacyPaymentData.value = undefined
}

const payPendingOrder = async (paymentMethodType: string): Promise<boolean> => {
  let mustContinue = true
  try {
    const paymentMethod = isYunoCheckout.value
      ? paymentMethodToSend(paymentMethodType)
      : paymentMethodType
    const payPendingInput = {
      orderId: appStore.currentOrder!.id,
      paymentMethod,
    }
    const payPendingDto: CreateOrder = await orderService.payPendingOrder({
      customerToken: authStore.token!,
      countryCode: appStore.countryCode,
      payPendingInput,
    })
    orderResponseDto.value = payPendingDto
  } catch (error: any) {
    debug('🚀 ~ file: CheckoutView.vue:463 ~ payPendingOrder ~ error:', error?.response || error)
    const { key } = handleStoreError(error)

    const relogin = key === StoreErrorType.status401
    const isCountryNotValid = key === StoreErrorType.status628
    const isDayPartError = key === StoreErrorType.status634

    if (!relogin) {
      if (!isCountryNotValid || !isDayPartError) {
        await handleCreateOrderErrors(error)
      } else {
        // Cancel order on isCountryNotValid or isDayPartError
        const onCancel = async () => {
          const orderId = appStore.currentOrder!.id
          await navigateTo(orderDetailRoute(orderId))
          appStore.setCurrentOrder(undefined)
        }
        const result = await showDialogAlert({
          dialog,
          data: {
            title: t(`errors.alerts.${key}.title`),
            body: t(`errors.alerts.${key}.body`),
            btnPrimary: t(`errors.alerts.${key}.accept`),
            btnSecondary: t(`errors.alerts.${key}.cancel`),
          },
        })
        if (result)
          await cancelOrder(
            {
              id: appStore.currentOrder!.id,
              isPaymentPending: isPreviousToPayment(appStore.currentOrder!),
            },
            onCancel,
            onCancel
          )
      }
      mustContinue = false
    }
  }
  return mustContinue
}

const shouldContinuePayment = async () => {
  // if not inPerson available continue payment
  if (!showInPersonPayment.value) return true
  const paymentMethodType = yunoMethod.value?.type || legacyPaymentMethod.value!.code
  // not in person selected, so continue
  if (paymentMethodType !== SpecialPaymentType.IN_PERSON) return true
  // current order is setted on createOrder just before this function call
  // navigate to ecommerce home (without await)
  navigateToHome()
  // open pickup modal
  appStore.pickupMethodModal = true
}

const onScroll = () => {
  const totalContent = document.querySelector('[data-test="cart-amount-row--total"]') as HTMLElement
  const overlayButtonPayment = document.querySelector(
    '[data-test="btn-checkout-pay"]'
  ) as HTMLElement
  if (!totalContent) return
  isVisibleTotal.value = !elementIsVisibleInScroll(totalContent, overlayButtonPayment)
}

const onScrollDebounced = debounce(onScroll, 250)

/* For logout on checkout */
watch(
  () => authStore.token,
  async () => {
    if (!authStore.token) {
      await navigateToHome()
    }
  }
)

/* For clearCart on checkout */
watch(cartProductsLength, async () => {
  if (!cartProductsLength.value && !appStore.currentOrder) {
    await navigateTo(cartRoute())
  }
})

/* Yappy toast message on checkout */
watch(yunoMethod, () => {
  if (yunoMethod.value && yunoMethod.value.type.toLowerCase() === 'yappy') {
    // show toast
    $toast.show({
      type: ToastType.INFO,
      duration: 8,
      content: t('checkout.sections.paymentMethods.toast.yappy'),
    })
  }
})

onMounted(async () => {
  fetching.value = true
  // VALIDATE - initialFetch is not loaded so we cannot enter checkout
  // VALIDATE - has cart.products length || currentPayingOrder or user is not logged
  // VALIDATE - phoneValidation is active and user need to verify phone
  if (
    !appStore.initialFetchLoaded ||
    (!appStore.currentOrder && !cartProductsLength.value) ||
    !authStore.token ||
    (appStore.phoneValidationActive && authStore.isNeededPhoneValidation)
  )
    return await navigateToHome()
  trackPageView({
    cg: GTMContentGroup.primary.ECOMMERCE,
    cg2: GTMContentGroup.secondary.PAYMENT,
  })

  // track add_shipping_info from order or cart
  const order = appStore.currentOrder
  if (order) trackAddShippingInfo({ cartProducts: undefined, order })
  else trackAddShippingInfo({ cartProducts: appStore.cart.products, order: undefined })

  // call to /cart one last time to check after create-order and payment
  await appStore.fetchCart()
  if (isYunoCheckout.value && !isFreeOrder.value) {
    await initYuno()
    await fetchYunoMethods()
    // set last card used as selected
    const lastSuccesfullyUsedMethod = paymentMethods.value.find(m => m.lastSuccessfullyUsed)
    if (lastSuccesfullyUsedMethod) selectMethod(lastSuccesfullyUsedMethod)
  }
  fetching.value = false

  if (isMobile?.value) {
    // Init - Detect if the total is visible on the page
    onScrollDebounced()
    // Execute the event when detecting Scroll on the page
    window.addEventListener('scroll', onScrollDebounced)
  }
})

const handlerAction = async () => {
  freeOrderConfirmation.value = undefined
  trackOnCloseModal()
  await navigateToHome()
}

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScrollDebounced, false)
})
</script>
<template>
  <template v-if="isMobile">
    <!-- Header -->
    <header class="sticky top-0 w-full bg-white md:hidden">
      <div class="flex w-fit flex-row items-center gap-2 py-2" @click="goBack">
        <img
          src="/images/icons/arrowUp.svg"
          class="h-auto w-8 -rotate-90 cursor-pointer"
          alt="back"
        />
        <h1 class="font-bold">{{ t('checkout.title') }}</h1>
      </div>
    </header>
    <!-- Payment button fixed bottom -->
    <section class="fixed bottom-0 left-0 z-10 w-full bg-white">
      <McdButton
        :disabled="disabledPay"
        data-test="btn-checkout-pay"
        is-primary
        class="h-16 w-full rounded-none"
        @on-click="processOrder"
      >
        {{ buttonLabel }}
      </McdButton>
    </section>
  </template>
  <section data-test="checkout" class="pb-16 md:px-4 md:pb-0">
    <div
      class="mx-auto max-w-[1280px] px-5 md:my-[5rem] md:w-[80%] md:px-12 lg:px-0 xl:min-w-[1100px]"
    >
      <h1 v-if="!isMobile" class="my-8 text-3xl font-bold">{{ t('checkout.title') }}</h1>
      <div class="mb-8 grid grid-cols-1 gap-4 md:my-4 lg:grid-cols-[1fr_360px] xl:gap-[6rem]">
        <section class="flex flex-col gap-6 md:gap-12">
          <!-- AddressCard component -->
          <LazyAddressCard v-if="addressCardProps" v-bind="addressCardProps" />

          <!-- Checkout alert user remoteness for checkout  -->
          <OrderDistanceStatus v-if="isMOP" />
          <!-- Payment methods -->
          <template v-if="!isFreeOrder">
            <LazyYunoCheckout
              v-if="isYunoCheckout"
              :loading="fetching"
              :methods="paymentMethods"
              :limit-enrollment="appStore.ecommerceConfig?.limitEnrollment"
              :in-person-payment="showInPersonPayment"
              :is-exclusive-payment="isExclusivePayment"
              @add-card="addCard"
              @select="selectMethod"
              @reload-methods="reloadYunoMethods"
            />
            <LazyLegacyCheckout
              v-else
              :loading="fetching"
              :methods="appStore.ecommerceConfig?.paymentMethodsDynamic || []"
              :in-person-payment="showInPersonPayment"
              :is-exclusive-payment="isExclusivePayment"
              @select="selectLegacyMethod"
            />
          </template>

          <!-- Tips -->
          <Tips v-if="mustShowTips" />

          <!-- Fiscal Fields -->
          <FiscalData
            v-if="mustShowFiscalFields"
            :fiscal="fiscalFields"
            :is-mandatory="mandatoryFiscalFields"
            :formatted-min-amount="appStore.formattedMinAmount"
            @set-fiscal="(fiscal: IMFiscalData | undefined) => { fiscalFields = fiscal }"
            @set-bill="(bill: Record<string, string> | undefined) => { billFields = bill }"
          />
        </section>
        <section class="flex w-full flex-col">
          <!-- CheckoutAlert -->
          <ProductAlert v-if="mustShowFiscalCheckoutAlert" :text="t('checkout.checkoutAlert')" />

          <!-- CartAmount -->
          <header class="mb-2 w-full text-base font-bold md:text-lg">
            {{ t('shoppingCart.summary') }}
          </header>
          <div class="rounded-md p-3 shadow-elevation-down">
            <CartAmount
              :order="appStore.currentOrder"
              :prices="totals"
              :tax="!!appStore.ecommerceConfig?.tax"
              :promotion-str="promoStr"
              :free-shipping-promo="hasFreeShippingPromo"
              :service-fee-name="restaurantStore.getServiceFeeName"
              :small-order-fee-name="restaurantStore.getSmallOrderFeeName"
            />
          </div>
          <!-- Order total visible -->
          <OrderTotalCollapsable v-if="isMobile" :prices="totals" :is-visible="isVisibleTotal" />
          <!-- Payment button -->
          <McdButton
            v-if="!isMobile"
            :disabled="disabledPay"
            data-test="btn-checkout-pay"
            is-primary
            class="my-4"
            @on-click="processOrder"
          >
            {{ buttonLabel }}
          </McdButton>
        </section>
      </div>
    </div>
  </section>
  <template v-if="isYunoCheckout">
    <section id="yuno-checkout"></section>
    <LazyYunoConfirmation
      v-if="yunoConfirmation"
      :confirmation="yunoConfirmation"
      :order="appStore.currentOrder"
      @retry="retryPayment"
      @change-card="changeCard"
    />
    <LazyYunoLoadingTransition v-if="paymentInProgress" />
  </template>
  <template v-else>
    <LazyLegacyPaymentIframe
      v-if="legacyPaymentData"
      :order-id="legacyPaymentData.orderId"
      :payment-url="legacyPaymentData.paymentUrl"
      :payment-method="legacyPaymentMethod"
      @result="onLegacyPaymentResult"
    />
    <LazyLegacyPaymentConfirmation
      v-if="legacyConfirmation"
      :result="legacyConfirmation"
      :order="appStore.currentOrder"
      @change-card="changeCardLegacy"
    />
    <LazyFreeOrderConfirmation
      v-if="freeOrderConfirmation"
      :loading="loading"
      @action="handlerAction"
    />
  </template>
  <LazyMCSonicBranding v-if="isPaymentSound" @finished="isPaymentSound = false" />
</template>
